import { type ReactNode } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
} from "framer-motion";
import {
  Bell,
  BellOff,
  BookOpenCheck,
  Bot,
  CalendarDays,
  CubeFocus,
  Landmark,
  LogOut,
  Newspaper,
  Settings,
  Users,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Tooltip } from "../ui/tooltip";

export const SIDEBAR_ICON_COMPONENTS = {
  bell: Bell,
  "bell-off": BellOff,
  "book-open-check": BookOpenCheck,
  bot: Bot,
  calendar: CalendarDays,
  "cube-focus": CubeFocus,
  landmark: Landmark,
  logout: LogOut,
  newspaper: Newspaper,
  settings: Settings,
  users: Users,
} as const;

export type SidebarIconName = keyof typeof SIDEBAR_ICON_COMPONENTS;

export interface SidebarNavItemProps {
  icon: SidebarIconName;
  label: string;
  isActive?: boolean;
  isCollapsed?: boolean;
  onClick: () => void;
  description?: string;
  badge?: ReactNode;
  className?: string;
  dataTourTarget?: string;
  isDanger?: boolean;
  isStateActive?: boolean;
  reservedTrailing?: boolean;
  tooltip?: string;
}

const activePillTransition: Transition = {
  type: "spring",
  stiffness: 520,
  damping: 42,
  mass: 0.72,
};

const quickTransition: Transition = {
  duration: 0.16,
  ease: [0, 0, 0.2, 1],
};

export function SidebarNavItem({
  icon,
  label,
  isActive = false,
  isCollapsed = false,
  onClick,
  description,
  badge,
  className,
  dataTourTarget,
  isDanger = false,
  isStateActive = false,
  reservedTrailing = false,
  tooltip,
}: SidebarNavItemProps) {
  const shouldReduceMotion = useReducedMotion();
  const Icon = SIDEBAR_ICON_COMPONENTS[icon];
  const layoutTransition = shouldReduceMotion
    ? { duration: 0 }
    : activePillTransition;
  const stateTransition = shouldReduceMotion
    ? { duration: 0 }
    : quickTransition;
  const hoverState = shouldReduceMotion ? undefined : "hover";

  const item = (
    <motion.button
      type="button"
      layout
      initial={false}
      animate="rest"
      whileHover={hoverState}
      whileTap={shouldReduceMotion ? undefined : { scale: 0.985 }}
      variants={{
        rest: { y: 0 },
        hover: { y: isActive ? 0 : -1 },
      }}
      transition={stateTransition}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      aria-label={label}
      data-active={isActive}
      data-collapsed={isCollapsed}
      data-danger={isDanger || undefined}
      data-state-active={isStateActive || undefined}
      data-tour-target={dataTourTarget}
      className={cn(
        "sidebar-nav-item__button",
        reservedTrailing &&
          !isCollapsed &&
          "sidebar-nav-item__button--reserved",
        className,
      )}
    >
      {isActive && (
        <motion.div
          layoutId="active-pill"
          className="sidebar-nav-item__active-pill fintheon-black-liquid-glass fintheon-glass-control--selected solvys-liquid-glass"
          transition={layoutTransition}
        />
      )}
      <motion.span
        className="sidebar-nav-item__icon"
        variants={{ rest: { scale: 1 }, hover: { scale: 1.08 } }}
        transition={layoutTransition}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </motion.span>
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.span
            key="label"
            className="sidebar-nav-item__copy"
            initial={shouldReduceMotion ? false : { opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: -4 }}
            transition={stateTransition}
          >
            <span className="sidebar-nav-item__label">{label}</span>
            {description && (
              <span className="sidebar-nav-item__description">
                {description}
              </span>
            )}
          </motion.span>
        )}
      </AnimatePresence>
      {badge && <span className="sidebar-nav-item__badge">{badge}</span>}
    </motion.button>
  );

  if (!isCollapsed) return item;

  return (
    <div className="sidebar-nav-item__tooltip-row">
      <Tooltip content={tooltip ?? label} side="right">
        {item}
      </Tooltip>
    </div>
  );
}
